import React, { useState, useEffect } from "react";
import { ActionButton, Modal, FormActions, Input, Select, IconsLarge } from "../../shared";
import type { SelectOption } from "../../shared";
import { authService, transactionsApi } from "../../../services";
import { BulkSaveConfirmModal } from "./BulkSaveConfirmModal";
import { TransactionScheduleControls } from "./TransactionScheduleControls";
import {
  buildInstallmentPreview,
  buildRecurrentPreview,
  buildRecurrencyEntriesFromScheduleRows,
  finalizeInstallmentRowsForSubmit,
  type ScheduleRowPreview,
} from "./transactionScheduleUtils";
import type { TransactionAttributes } from "../../../types";
import type { BankAccountAttributes } from "../../../types";
import type { CostCenterAttributes } from "../../../types";
import type { SubcategoryAttributes } from "../../../types";
import {
  competenceDateToApiFirstOfMonth,
  competenceDateToMonthValue,
  monthValueToCompetenceApiDate,
} from "../../../utils/competenceDateUtils";

type BulkUpdateFields = Partial<
  Omit<TransactionAttributes, "id" | "companyId" | "createdAt" | "updatedAt">
>;

const PAYMENT_METHOD_OPTIONS: SelectOption[] = [
  { value: "", label: "Nenhum" },
  { value: "Dinheiro", label: "Dinheiro" },
  { value: "Pix", label: "Pix" },
  { value: "Cartão de Crédito", label: "Cartão de Crédito" },
  { value: "Cartão de Débito", label: "Cartão de Débito" },
];

interface CreateInputTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  companyId: number;
  subcategories: SubcategoryAttributes[];
  bankAccounts: BankAccountAttributes[];
  costCenters: CostCenterAttributes[];
  editingTransaction?: TransactionAttributes | null;
  /** When set, modal is in bulk-edit mode: same form applies to all these transaction IDs. */
  transactionIds?: number[];
  /** True while subcategories are being fetched by type. */
  subcategoriesLoading?: boolean;
}

export const CreateInputTransactionModal: React.FC<CreateInputTransactionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  companyId,
  subcategories,
  bankAccounts,
  costCenters,
  editingTransaction,
  transactionIds,
  subcategoriesLoading = false,
}) => {
  const isBulk = transactionIds != null && transactionIds.length > 0;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subcategoryId, setSubcategoryId] = useState<number | "">("");
  const [bankAccountId, setBankAccountId] = useState<number | "">("");
  const [costCenterId, setCostCenterId] = useState<number | "">("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [description, setDescription] = useState("");
  const [observation, setObservation] = useState("");
  const [competenceDate, setCompetenceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [installmentEnabled, setInstallmentEnabled] = useState(false);
  const [recurrentEnabled, setRecurrentEnabled] = useState(false);
  const [installmentCount, setInstallmentCount] = useState("2");
  const [recurrentMonths, setRecurrentMonths] = useState("2");
  const [installmentDraftRows, setInstallmentDraftRows] = useState<ScheduleRowPreview[] | null>(null);
  const [product, setProduct] = useState("");
  const [client, setClient] = useState("");
  const [extraFieldsExpanded, setExtraFieldsExpanded] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkConfirmLoading, setBulkConfirmLoading] = useState(false);
  const [bulkConfirmError, setBulkConfirmError] = useState<string | null>(null);
  const [pendingBulkPayload, setPendingBulkPayload] = useState<BulkUpdateFields | null>(null);

  const subcategoryOptions: SelectOption[] = subcategories.map((s) => ({
    value: s.id,
    label: s.name,
  }));
  const bankAccountOptions: SelectOption[] = bankAccounts.map((a) => ({
    value: a.id,
    label: a.name,
  }));
  const costCenterOptions: SelectOption[] = costCenters.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  useEffect(() => {
    if (editingTransaction && !isBulk) {
      setSubcategoryId(editingTransaction.subcategoryId);
      setBankAccountId(editingTransaction.bankAccountId);
      setCostCenterId(editingTransaction.costCenterId ?? "");
      setAmount(String(editingTransaction.amount));
      setPaymentMethod(editingTransaction.paymentMethod ?? "");
      setDescription(editingTransaction.description ?? "");
      setObservation(editingTransaction.observation ?? "");
      setCompetenceDate(
        competenceDateToApiFirstOfMonth(editingTransaction.competenceDate ?? "")
      );
      setDueDate(editingTransaction.dueDate ?? "");
      setPaymentDate(editingTransaction.paymentDate ?? "");
      setInstallmentEnabled(false);
      setRecurrentEnabled(false);
      setInstallmentCount("2");
      setRecurrentMonths("2");
      setInstallmentDraftRows(null);
      setProduct(editingTransaction.product ?? "");
      setClient(editingTransaction.client ?? "");
      setExtraFieldsExpanded(
        Boolean(
          (editingTransaction.observation ?? "").trim() ||
            (editingTransaction.product ?? "").trim() ||
            (editingTransaction.client ?? "").trim()
        )
      );
    } else if (isBulk) {
      setSubcategoryId("");
      setBankAccountId("");
      setCostCenterId("");
      setAmount("");
      setPaymentMethod("");
      setDescription("");
      setObservation("");
      setCompetenceDate("");
      setDueDate("");
      setPaymentDate("");
      setInstallmentEnabled(false);
      setRecurrentEnabled(false);
      setInstallmentCount("2");
      setRecurrentMonths("2");
      setInstallmentDraftRows(null);
      setProduct("");
      setClient("");
      setExtraFieldsExpanded(false);
    } else {
      setSubcategoryId(subcategories[0]?.id ?? "");
      setBankAccountId(bankAccounts[0]?.id ?? "");
      setCostCenterId("");
      setAmount("");
      setPaymentMethod("");
      setDescription("");
      setObservation("");
      setCompetenceDate("");
      setDueDate("");
      setPaymentDate("");
      setInstallmentEnabled(false);
      setRecurrentEnabled(false);
      setInstallmentCount("2");
      setRecurrentMonths("2");
      setInstallmentDraftRows(null);
      setProduct("");
      setClient("");
      setExtraFieldsExpanded(false);
    }
    setError(null);
  }, [editingTransaction, isOpen, isBulk, subcategories, bankAccounts, costCenters]);

  const handleSubmit = async () => {
    const subId = typeof subcategoryId === "number" ? subcategoryId : Number(subcategoryId);
    const bankId = typeof bankAccountId === "number" ? bankAccountId : Number(bankAccountId);
    const costId = typeof costCenterId === "number" ? costCenterId : Number(costCenterId);
    const amountNum = parseFloat(amount);

    if (isBulk) {
      const bulkPayload: BulkUpdateFields = {};
      if (subId && !Number.isNaN(subId)) bulkPayload.subcategoryId = subId;
      if (bankId && !Number.isNaN(bankId)) bulkPayload.bankAccountId = bankId;
      if (costId && !Number.isNaN(costId)) bulkPayload.costCenterId = costId;
      if (amount.trim() !== "" && !Number.isNaN(amountNum)) bulkPayload.amount = amountNum;
      if (paymentMethod.trim()) bulkPayload.paymentMethod = paymentMethod.trim();
      if (description.trim()) bulkPayload.description = description.trim();
      if (observation.trim()) bulkPayload.observation = observation.trim();
      if (competenceDate.trim()) bulkPayload.competenceDate = competenceDate.trim();
      if (dueDate.trim()) bulkPayload.dueDate = dueDate.trim();
      if (paymentDate.trim()) bulkPayload.paymentDate = paymentDate.trim();
      if (product.trim()) bulkPayload.product = product.trim();
      if (client.trim()) bulkPayload.client = client.trim();

      if (Object.keys(bulkPayload).length === 0) {
        setError("Preencha pelo menos um campo para atualizar as transações selecionadas.");
        return;
      }

      setPendingBulkPayload(bulkPayload);
      setBulkConfirmError(null);
      setBulkConfirmOpen(true);
      return;
    }

    if (!subId || !bankId) {
      setError("Categoria e conta são obrigatórios.");
      return;
    }
    if (Number.isNaN(amountNum)) {
      setError("Valor deve ser um número.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const costCenterValue =
        costId && !Number.isNaN(costId) ? costId : null;
      const commonFields = {
        subcategoryId: subId,
        bankAccountId: bankId,
        costCenterId: costCenterValue,
        type: "input" as const,
        paymentMethod: paymentMethod.trim() || null,
        description: description.trim() || null,
        observation: observation.trim() || null,
        product: product.trim() || null,
        client: client.trim() || null,
      };

      const scheduleDisabled = !!editingTransaction || isBulk;

      if (!scheduleDisabled && installmentEnabled) {
        const n = parseInt(installmentCount, 10);
        if (!dueDate.trim()) {
          setError("Informe a data de vencimento da primeira parcela.");
          setLoading(false);
          return;
        }
        if (Number.isNaN(n) || n < 2 || n > 120) {
          setError("Número de parcelas deve ser entre 2 e 120.");
          setLoading(false);
          return;
        }
        const rawRows =
          installmentDraftRows ??
          buildInstallmentPreview(amountNum, n, dueDate, competenceDate, paymentDate);
        if (!rawRows?.length) {
          setError("Não foi possível gerar as parcelas. Verifique valor e datas.");
          setLoading(false);
          return;
        }
        const rows = finalizeInstallmentRowsForSubmit(rawRows, paymentDate);
        const invalidRow = rows.find(
          (r) =>
            !r.dueDate.trim() ||
            !/^\d{4}-\d{2}-\d{2}$/.test(r.dueDate.trim()) ||
            !(r.amount > 0)
        );
        if (invalidRow) {
          setError(
            "Cada parcela precisa de vencimento válido (AAAA-MM-DD) e valor maior que zero. Abra Revisar parcelas e ajuste."
          );
          setLoading(false);
          return;
        }
        const response = await transactionsApi.create({
          ...commonFields,
          companyId,
          amount: amountNum,
          competenceDate: competenceDate.trim() || null,
          dueDate: dueDate.trim() || null,
          paymentDate: paymentDate.trim() || null,
          installment: null,
          installments: rows.map((r) => ({
            amount: r.amount,
            dueDate: r.dueDate.trim(),
          })),
        });
        if (response.success) {
          onSuccess();
          onClose();
        } else {
          setError(response.error ?? "Falha ao criar transação com parcelas");
        }
        setLoading(false);
        return;
      }

      if (!scheduleDisabled && recurrentEnabled) {
        const n = parseInt(recurrentMonths, 10);
        if (!dueDate.trim()) {
          setError("Informe a data de vencimento da primeira repetição.");
          setLoading(false);
          return;
        }
        if (Number.isNaN(n) || n < 2 || n > 120) {
          setError("Número de meses deve ser entre 2 e 120.");
          setLoading(false);
          return;
        }
        const rows = buildRecurrentPreview(
          amountNum,
          n,
          dueDate,
          competenceDate,
          paymentDate
        );
        if (!rows?.length) {
          setError("Não foi possível gerar a recorrência. Verifique valor e datas.");
          setLoading(false);
          return;
        }
        const invalidRecurrent = rows.find(
          (r) =>
            !r.dueDate.trim() ||
            !/^\d{4}-\d{2}-\d{2}$/.test(r.dueDate.trim()) ||
            !(r.amount > 0) ||
            !String(r.competenceDate ?? r.dueDate).trim() ||
            !/^\d{4}-\d{2}-\d{2}$/.test(String(r.competenceDate ?? r.dueDate).trim())
        );
        if (invalidRecurrent) {
          setError(
            "Recorrência: cada repetição precisa de competência e vencimento válidos (AAAA-MM-DD) e valor maior que zero."
          );
          setLoading(false);
          return;
        }
        const first = rows[0];
        const response = await transactionsApi.create({
          ...commonFields,
          companyId,
          amount: amountNum,
          competenceDate: first.competenceDate,
          dueDate: first.dueDate,
          paymentDate: first.paymentDate,
          installment: null,
          recurrency: { entries: buildRecurrencyEntriesFromScheduleRows(rows) },
        });
        if (response.success) {
          onSuccess();
          onClose();
        } else {
          setError(response.error ?? "Falha ao criar transação com recorrência");
        }
        setLoading(false);
        return;
      }

      const payload = {
        ...commonFields,
        amount: amountNum,
        competenceDate: competenceDate.trim() || null,
        dueDate: dueDate.trim() || null,
        paymentDate: paymentDate.trim() || null,
        installment: editingTransaction?.installment ?? null,
      };

      if (editingTransaction) {
        const response = await transactionsApi.update(editingTransaction.id, payload);
        if (response.success) {
          onSuccess();
          onClose();
        } else {
          setError(response.error ?? "Falha ao atualizar transação");
        }
      } else {
        const response = await transactionsApi.create({
          ...payload,
          companyId,
        });
        if (response.success) {
          onSuccess();
          onClose();
        } else {
          setError(response.error ?? "Falha ao criar transação");
        }
      }
    } catch {
      setError(
        isBulk
          ? "Falha ao atualizar transações"
          : editingTransaction
            ? "Falha ao atualizar transação"
            : "Falha ao criar transação"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setBulkConfirmOpen(false);
    setBulkConfirmError(null);
    setPendingBulkPayload(null);
    setBulkConfirmLoading(false);
    onClose();
  };

  const handleBulkConfirmPassword = async (password: string) => {
    setBulkConfirmLoading(true);
    setBulkConfirmError(null);
    const user = authService.getUserData();
    if (!user?.email) {
      setBulkConfirmError("Não foi possível identificar seu usuário. Faça login novamente.");
      setBulkConfirmLoading(false);
      return;
    }
    const authRes = await authService.login({ email: user.email, password });
    if (!authRes.success) {
      setBulkConfirmError(authRes.error ?? "Senha incorreta.");
      setBulkConfirmLoading(false);
      return;
    }
    const payload = pendingBulkPayload;
    const ids = transactionIds;
    if (!payload || !ids?.length) {
      setBulkConfirmLoading(false);
      return;
    }
    try {
      const response = await transactionsApi.updateBulk(ids, payload);
      if (response.success) {
        setBulkConfirmOpen(false);
        setPendingBulkPayload(null);
        onSuccess();
        onClose();
      } else {
        setBulkConfirmError(response.error ?? "Falha ao atualizar transações.");
      }
    } catch {
      setBulkConfirmError("Falha ao atualizar transações.");
    } finally {
      setBulkConfirmLoading(false);
    }
  };

  const handleBulkConfirmModalClose = () => {
    if (bulkConfirmLoading) return;
    setBulkConfirmOpen(false);
    setBulkConfirmError(null);
    setPendingBulkPayload(null);
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        isBulk
          ? `Editar em lote (${transactionIds!.length} transações)`
          : editingTransaction
            ? "Editar entrada"
            : "Nova entrada"
      }
      icon={<IconsLarge.Receipt />}
      size="lg"
    >
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 mb-6">
          <span className="text-xl">⚠️</span>
          <span className="text-red-800 font-medium">{error}</span>
        </div>
      )}

      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Categoria"
            value={subcategoryId}
            onChange={(v) => setSubcategoryId(Number(v))}
            options={subcategoryOptions}
            placeholder={subcategoriesLoading ? "Carregando..." : "Selecione"}
            disabled={loading || subcategoriesLoading}
            required
            searchable={subcategoryOptions.length > 6}
          />
          <Select
            label="Conta"
            value={bankAccountId}
            onChange={(v) => setBankAccountId(Number(v))}
            options={bankAccountOptions}
            placeholder="Selecione"
            disabled={loading}
            required
            searchable={bankAccountOptions.length > 6}
          />
          <Select
            label="Centro de custo"
            value={costCenterId}
            onChange={(v) => setCostCenterId(v === "" ? "" : Number(v))}
            options={[{ value: "", label: "Nenhum" }, ...costCenterOptions]}
            placeholder="Opcional"
            disabled={loading}
            searchable={costCenterOptions.length > 6}
          />
          <Input
            label="Valor (R$)"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            step={1}
            disabled={loading}
            required
            placeholder="0,00"
          />
          <Select
            label="Forma de pagamento"
            value={paymentMethod}
            onChange={(v) => setPaymentMethod(String(v))}
            options={PAYMENT_METHOD_OPTIONS}
            placeholder="Selecione"
            disabled={loading}
            searchable={false}
          />
          <Input
            label="Descrição"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={loading}
            placeholder="Descrição da transação"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Input
            label="Competência (mês/ano)"
            type="month"
            value={competenceDateToMonthValue(competenceDate)}
            onChange={(e) =>
              setCompetenceDate(monthValueToCompetenceApiDate(e.target.value))
            }
            disabled={loading}
          />
          <Input
            label="Vencimento"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            disabled={loading}
          />
          <Input
            label="Data pagamento"
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            disabled={loading}
          />
        </div>

        {!isBulk && (
          <TransactionScheduleControls
            disabled={loading || !!editingTransaction}
            installmentEnabled={installmentEnabled}
            onInstallmentEnabledChange={setInstallmentEnabled}
            recurrentEnabled={recurrentEnabled}
            onRecurrentEnabledChange={setRecurrentEnabled}
            installmentCount={installmentCount}
            onInstallmentCountChange={setInstallmentCount}
            recurrentMonths={recurrentMonths}
            onRecurrentMonthsChange={setRecurrentMonths}
            dueDate={dueDate}
            competenceDate={competenceDate}
            paymentDate={paymentDate}
            amountStr={amount}
            installmentDraftRows={installmentDraftRows}
            onInstallmentDraftChange={setInstallmentDraftRows}
          />
        )}

        <div className="border border-gray-200 dark:border-[#374151] rounded-xl overflow-hidden">
          <ActionButton
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setExtraFieldsExpanded((e) => !e)}
            className="w-full !rounded-none border-0 shadow-none justify-between px-4 py-3 text-left text-sm bg-gray-50 dark:bg-[#1A202C]/80 hover:bg-gray-100 dark:hover:bg-[#374151] text-gray-700 dark:text-slate-300 font-medium"
          >
            <span>Observação, produto e cliente</span>
            <span className="text-gray-500 dark:text-slate-400 shrink-0" aria-hidden>
              {extraFieldsExpanded ? "▲ Recolher" : "▼ Expandir"}
            </span>
          </ActionButton>
          {extraFieldsExpanded && (
            <div className="p-4 space-y-4 border-t border-gray-200 dark:border-[#374151] bg-white dark:bg-[#2D3748]/50">
              <Input
                label="Observação"
                type="text"
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                disabled={loading}
                placeholder="Observações"
              />
              <Input
                label="Produto ou serviço"
                type="text"
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                disabled={loading}
                placeholder="Produto ou serviço"
              />
              <Input
                label="Cliente ou fornecedor"
                type="text"
                value={client}
                onChange={(e) => setClient(e.target.value)}
                disabled={loading}
                placeholder="Cliente ou fornecedor"
              />
            </div>
          )}
        </div>

        <FormActions
          onCancel={handleClose}
          onSubmit={handleSubmit}
          loading={loading}
          isEditing={!!editingTransaction || isBulk}
          submitType="button"
          submitText={
            isBulk
              ? "Atualizar Lançamentos"
              : editingTransaction
                ? "Atualizar entrada"
                : "Criar entrada"
          }
        />
      </div>
    </Modal>
    <BulkSaveConfirmModal
      isOpen={bulkConfirmOpen}
      transactionCount={transactionIds?.length ?? 0}
      loading={bulkConfirmLoading}
      error={bulkConfirmError}
      onClose={handleBulkConfirmModalClose}
      onConfirm={handleBulkConfirmPassword}
    />
    </>
  );
};
